import { Loader2, Terminal } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Modal } from '@/components/ui/modal'
import { Textarea } from '@/components/ui/textarea'
import { useSetServerSSH, type CustomerServer } from './api'
import { useVaultRetry } from './useVaultRetry'

const USER_RE = /^[a-z_][a-z0-9_-]{0,31}$/
const HOST_KEY_RE = /^SHA256:[A-Za-z0-9+/]{43}$/

/** SSH erişimi: özel anahtar sunucuya bir kez gönderilir, şifreli saklanır ve bir daha gösterilmez. */
export function ServerSSHModal({ server, onClose }: { server: CustomerServer; onClose: () => void }) {
  const { t } = useTranslation()
  const save = useSetServerSSH()
  const vault = useVaultRetry()
  const [user, setUser] = useState(server.ssh_user || 'deploy')
  const [port, setPort] = useState(String(server.ssh_port || 22))
  const [hostKey, setHostKey] = useState(server.ssh_host_key_fingerprint)
  const [key, setKey] = useState('')
  const portN = Number(port)
  const valid =
    USER_RE.test(user) &&
    Number.isInteger(portN) &&
    portN >= 1 &&
    portN <= 65535 &&
    HOST_KEY_RE.test(hostKey.trim()) &&
    key.includes('PRIVATE KEY')

  const submit = () =>
    vault.run(async () => {
      await save.mutateAsync({
        id: server.id,
        ssh_user: user,
        ssh_port: portN,
        host_key_fingerprint: hostKey.trim(),
        private_key: key,
      })
      setKey('')
      toast.success(t('updates.ssh.saved'))
      onClose()
    })

  return (
    <>
      <Modal
        open
        onOpenChange={(v) => !v && onClose()}
        dismissible={!save.isPending}
        size="lg"
        icon={<Terminal />}
        title={t('updates.ssh.title', { name: server.name })}
        description={t('updates.ssh.hint')}
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
        footer={
          <>
            <Button type="button" variant="cancel" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!valid || save.isPending}>
              {save.isPending ? <Loader2 className="animate-spin" /> : null}
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="ssh-user">{t('updates.ssh.user')}</Label>
              <Input
                id="ssh-user"
                autoComplete="off"
                value={user}
                onChange={(e) => setUser(e.target.value)}
                aria-invalid={!USER_RE.test(user)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ssh-port">{t('updates.ssh.port')}</Label>
              <Input id="ssh-port" inputMode="numeric" value={port} onChange={(e) => setPort(e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="ssh-hostkey">{t('updates.ssh.hostKey')}</Label>
            <Input
              id="ssh-hostkey"
              className="font-mono text-xs"
              placeholder="SHA256:…"
              value={hostKey}
              onChange={(e) => setHostKey(e.target.value)}
            />
            <p className="text-muted-foreground text-xs">{t('updates.ssh.hostKeyHint')}</p>
            <code className="bg-muted block rounded px-2 py-1 text-xs">
              ssh-keygen -lf /etc/ssh/ssh_host_ed25519_key.pub
            </code>
          </div>
          <div className="space-y-2">
            <Label htmlFor="ssh-key">{t('updates.ssh.privateKey')}</Label>
            <Textarea
              id="ssh-key"
              rows={6}
              className="font-mono text-xs"
              autoComplete="off"
              spellCheck={false}
              placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
              value={key}
              onChange={(e) => setKey(e.target.value)}
            />
            <p className="text-muted-foreground text-xs">
              {server.has_ssh_key ? t('updates.ssh.keyReplace') : t('updates.ssh.keyHint')}
            </p>
          </div>
        </div>
      </Modal>
      {vault.modal}
    </>
  )
}
